import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { fetchApplicationDetails, uploadFormFile, saveFormData } from '../../../utils/apiUtils';
import FilePreviewModal from '../../common/FilePreviewModal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import FormPageNavigator from '../../common/FormPageNavigator';
import SessionTimer from '../../common/SessionTimer';

// Helper function to get file name
const getFileName = (file: File | string | null): string => {
  if (file instanceof File) {
    return file.name;
  }
  if (typeof file === 'string') {
    const parts = file.split(/[\/\\]/);
    return parts[parts.length - 1];
  }
  return 'No file selected';
};

// Helper function to generate a unique application ID
const generateApplicationId = (): string => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
  const year = String(now.getFullYear());
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
  const randomChar = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 32)];
  return `CNG-${day}${month}${year}${hours}${minutes}${seconds}-${milliseconds}${randomChar}`;
};

// A4 Dimensions (approximate for screen, maintaining aspect ratio)
const A4_WIDTH_PX = 794; // Approx 210mm at 96 DPI
const A4_HEIGHT_PX = 1123; // Approx 297mm at 96 DPI

// Progress bar at top of page
// (handler moved into component below)

const ProgressBarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => `${props.progress}%`};
  background-color: #6cb288;
  transition: width 0.5s ease-in-out;
`;

const ProgressLabel = styled.div`
  position: fixed;
  top: 10px;
  right: 10px;
  background-color: #f8fcfa;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 1000;
`;

// Zoom controls
const ZoomControlsContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
`;

const ZoomButton = styled.button`
  width: 30px;
  height: 30px;
  background-color: white;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  padding: 0;
  
  &:hover {
    background-color: #f8fcfa;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const OfflineIndicator = styled.div`
  background-color: #dc3545;
  color: white;
  text-align: center;
  padding: 10px;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2000;
  font-size: 0.9em;
`;

const PageWrapper = styled.div<{ scale: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  min-height: 100vh;
  background-color: #f5f5f5;
  transform: scale(${props => props.scale});
  transform-origin: center top;
  transition: transform 0.3s ease;
`;

const A4PageContainer = styled.div`
  width: ${A4_WIDTH_PX}px;
  min-height: ${A4_HEIGHT_PX}px;
  background-color: white;
  padding: 50px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07);
  box-sizing: border-box;
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #212529;
  font-size: 13px;
  line-height: 1.6;
`;

const FloatingBackButton = styled.button`
  position: fixed;
  bottom: 30px;
  left: 30px;
  background: linear-gradient(145deg, #4a6cf7, #5e54e2);
  color: white;
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(74, 108, 247, 0.3), 0 3px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 10px 20px rgba(74, 108, 247, 0.4), 0 6px 6px rgba(0, 0, 0, 0.1);
    
    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0) scale(0.98);
    box-shadow: 0 3px 8px rgba(74, 108, 247, 0.2), 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const FormContentContainer = styled.div`
  /* Form content styling */
`;

const PageNumber = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  font-size: 0.8em;
  color: #777;
`;

const NextPageButton = styled.button`
  position: absolute;
  bottom: 15px;
  right: 60px;
  background-color: #6cb288;
  color: white;
  padding: 8px 25px;
  border: none;
  border-radius: 4px;
  font-size: 1em;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #4d9e70;
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const SaveIndicator = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 50%;
  left: 20px;
  transform: translateY(-50%);
  background-color: #f8fcfa;
  color: #6cb288;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #a8d0b9;
  font-size: 0.8em;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 10000;
`;

const SaveDraftButton = styled.button`
  background-color: #ffc107;
  color: #212529;
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 15px;
  margin-right: 10px;
  display: inline-block;
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #e0a800;
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #c69500;
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }

  &:disabled {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
  }
`;

const PayNowButton = styled.button`
  background-color: #007bff;
  color: white;
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 30px;
  display: block; 
  margin-left: auto;
  margin-right: auto;
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #0069d9;
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #0056b3;
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }
`;

const ActionButtonsContainer = styled.div`
  margin-top: 30px;
  width: 100%;
`;

// Form specific styled components
const FormTitle = styled.h2`
  font-size: 1.5em;
  font-weight: 600;
  margin-bottom: 20px;
  text-align: center;
  color: #333;
`;

const FileUploadContainer = styled.div`
  margin-bottom: 20px;
`;

const FileLabel = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
  font-size: 0.95em;
`;

const FileInput = styled.input`
  display: none;
`;

const FileUploadButton = styled.label`
  display: inline-block;
  padding: 8px 16px;
  background-color: #6cb288;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #4d9e70;
  }
`;

const FileName = styled.span`
  margin-left: 12px;
  color: #666;
  font-size: 0.9em;
`;

const NotesContainer = styled.div`
  margin-top: 40px;
  padding: 20px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
`;

const NotesTitle = styled.h3`
  font-size: 1.1em;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
`;

const NotesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NoteItem = styled.li`
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
  color: #555;
  font-size: 0.9em;
  line-height: 1.5;
  
  &:before {
    content: "•";
    position: absolute;
    left: 0;
    color: #6cb288;
    font-weight: bold;
  }
`;

interface FormData {
  applicationNoOfficeUse: string;
  naOrderCopy: File | string | null;
  partitionDistributionDeed: File | string | null;
  firmAllotmentLetter: File | string | null;
  poaAffidavit: File | string | null;
  dateOfBirthProof: File | string | null;
  residenceProof: File | string | null;
  educationalQualificationProof: File | string | null;
  photoIdAddressProof: File | string | null;
  errors?: {
    naOrderCopy?: string;
    partitionDistributionDeed?: string;
    firmAllotmentLetter?: string;
    poaAffidavit?: string;
    dateOfBirthProof?: string;
    residenceProof?: string;
    educationalQualificationProof?: string;
    photoIdAddressProof?: string;
  };
}

interface ApplicationFormPage6Props {
  formData?: any;
  isViewMode?: boolean;
}

const initialState: FormData = {
  applicationNoOfficeUse: '',
  naOrderCopy: null,
  partitionDistributionDeed: null,
  firmAllotmentLetter: null,
  poaAffidavit: null,
  dateOfBirthProof: null,
  residenceProof: null,
  educationalQualificationProof: null,
  photoIdAddressProof: null,
  errors: {}
};

const ApplicationFormPage6: React.FC<ApplicationFormPage6Props> = ({ isViewMode = false, formData: propFormData }) => {
  const formRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [applicationId, setApplicationId] = useState<string>('');
  const isNewlyGeneratedIdRef = useRef<boolean>(false);
  
  // Progress bar state
  const [formProgress, setFormProgress] = useState<number>(0);
  
  // Zoom state
  const [zoomScale, setZoomScale] = useState<number>(1.0);
  
  // Check if Fill Dummy Data button should be shown (controlled from Admin Controls, default OFF)
  const [showDummyDataButton, setShowDummyDataButton] = useState<boolean>(false);
  
  // Fetch showDummyDataButton setting from backend
  useEffect(() => {
    const fetchDummyDataSetting = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const API_BASE = API_URL === '/api' ? '' : API_URL;
        const response = await fetch(`${API_BASE}/api/public/settings`);
        if (response.ok) {
          const data = await response.json();
          const settings = data.settings || data;
          if (settings && typeof settings.showDummyDataButton === 'boolean') {
            setShowDummyDataButton(settings.showDummyDataButton);
          }
        }
      } catch (error) {
        console.error('Error fetching dummy data button setting:', error);
      }
    };
    fetchDummyDataSetting();
  }, []);
  
  // Form data state
  const [formData, setFormData] = useState<FormData>(propFormData || { ...initialState });

  // State for save indicator
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<string>('Saved');
  const [showSaveIndicator, setShowSaveIndicator] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalFileUrl, setModalFileUrl] = useState<string | null>(null);
  const [modalFileName, setModalFileName] = useState<string | null>(null);
  const [modalFileType, setModalFileType] = useState<string | null>(null);
  
  // Save timeout reference for debouncing
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // File upload states
  type UploadableField = keyof Pick<FormData, 
    'naOrderCopy' | 'partitionDistributionDeed' | 'firmAllotmentLetter' | 'poaAffidavit' | 
    'dateOfBirthProof' | 'residenceProof' | 'educationalQualificationProof' | 'photoIdAddressProof'
  >;

  const [uploadStatus, setUploadStatus] = useState<{[key in UploadableField]?: 'idle' | 'uploading' | 'success' | 'error'}>({});
  const [uploadProgress, setUploadProgress] = useState<{[key in UploadableField]?: number}>({});
  const [uploadMessage, setUploadMessage] = useState<{[key in UploadableField]?: string}>({});

  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);

  const handleZoomIn = () => setZoomScale(prev => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setZoomScale(prev => Math.max(prev - 0.1, 0.5));

  const handleNextButtonClick = async () => {
    // Save draft before navigation
    if (applicationId && isOnline) {
      console.log('[Navigation Page6] Saving draft before navigating');
      await saveToDatabaseCallback({ ...formData, applicationNoOfficeUse: applicationId }, true, applicationId);
    }
    navigate('/form/page7');
  };

  const handleOpenFilePreview = (file: File | string | null, title: string) => {
    if (!file) return;
    
    let url = '';
    let type = '';
    let name = '';
    
    if (file instanceof File) {
      url = URL.createObjectURL(file);
      type = file.type;
      name = file.name;
    } else if (typeof file === 'string') {
      // Determine backend origin consistently using REACT_APP_API_URL if absolute; otherwise fallback to current origin
      const rawApiBaseUrl = process.env.REACT_APP_API_URL || '/api';
      let serverBaseForFiles = window.location.origin;
      if (rawApiBaseUrl.startsWith('http')) {
        try {
          const parsed = new URL(rawApiBaseUrl);
          serverBaseForFiles = `${parsed.protocol}//${parsed.host}`;
        } catch (e) {
          console.warn('[handleOpenFilePreview] Could not parse REACT_APP_API_URL, using window.origin instead:', e);
          serverBaseForFiles = window.location.origin;
        }
      } else {
        // Relative API URL - check if we're in development
        const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (isDevelopment && window.location.port === '3000') {
          serverBaseForFiles = `${window.location.protocol}//${window.location.hostname}:5000`;
        }
      }
      
      // Normalize to a path (strip any accidental origin and ensure leading slash)
      let normalizedPath = file;
      if (file.includes('://')) {
        try { normalizedPath = new URL(file).pathname; } catch { normalizedPath = file; }
      }
      if (!normalizedPath.startsWith('/')) {
        normalizedPath = `/${normalizedPath}`;
      }
      
      url = `${serverBaseForFiles}${normalizedPath}`;
      
      // Extract just the filename from the path
      name = normalizedPath.split('/').pop() || '';
      
      // Keep display name transformation based on title
      const formattedFieldName = title
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join('');
      
      // If the filename starts with FILE_, extract the application ID part and present nicer name
      if (name.startsWith('FILE_')) {
        const applicationIdFromName = name.replace(/^FILE_/, '').replace(/\.[^.]+$/, '');
        const extension = name.split('.').pop() || '';
        name = `${formattedFieldName}_${applicationIdFromName}.${extension}`;
      }
      
      const ext = (name.split('.').pop() || '').toLowerCase();
      if (ext === 'pdf') {
        type = 'application/pdf';
      } else if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
        type = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      } else {
        type = 'application/octet-stream';
      }
    }
    
    if (url) {
      setModalFileUrl(url);
      setModalFileName(name);
      setModalFileType(type);
      setIsModalOpen(true);
    }
  };

  // Effect to initialize or generate applicationId
  useEffect(() => {
    if (propFormData?.applicationNoOfficeUse) {
      setApplicationId(propFormData.applicationNoOfficeUse);
      return;
    }

    let currentApplicationId = location.state?.applicationId || sessionStorage.getItem('currentApplicationId');
    let source = '';

    if (currentApplicationId) {
      source = location.state?.applicationId ? 'location.state' : 'sessionStorage';
      console.log(`[ApplicationFormPage6 Effect InitID] Found existing Application ID: ${currentApplicationId} from ${source}`);
      setApplicationId(currentApplicationId);
      if (formData.applicationNoOfficeUse !== currentApplicationId) {
        setFormData(prev => ({ ...prev, applicationNoOfficeUse: currentApplicationId }));
      }
    } else {
      const newId = generateApplicationId();
      console.log(`[ApplicationFormPage6 Effect InitID] No existing ID found. Generated new Application ID: ${newId}`);
      setApplicationId(newId);
      sessionStorage.setItem('currentApplicationId', newId);
      isNewlyGeneratedIdRef.current = true;
      setFormData(prev => ({ ...prev, applicationNoOfficeUse: newId }));
    }
  }, [location.state, propFormData]);

  // Effect to load existing application data when resuming a draft
  useEffect(() => {
    if (propFormData) return; // Skip loading if formData is passed as a prop

    const loadDraftData = async () => {
      if (applicationId && !isNewlyGeneratedIdRef.current && !isViewMode) {
        console.log(`[ApplicationFormPage6 LoadDraft] Attempting to load draft data for ID: ${applicationId}`);
        try {
          const appData = await fetchApplicationDetails(applicationId);
          if (appData && appData.formData) {
            const loadedFormData = {
              ...initialState,
              ...appData.formData,
            };
            setFormData(loadedFormData);
            console.log(`[ApplicationFormPage6 LoadDraft] Successfully loaded draft data for ID: ${applicationId}`, appData);
          } else {
            console.warn(`[ApplicationFormPage6 LoadDraft] No application data or formData found for ID: ${applicationId}`, appData);
          }
        } catch (error) {
          console.error(`[ApplicationFormPage6 LoadDraft] Error loading draft data for ${applicationId}:`, error);
        }
      }
    };

    loadDraftData();
  }, [applicationId, isViewMode, propFormData]);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handleDownloadPdf = async () => {
    setIsPdfGenerating(true);
    setCompressionProgress(0);
    const formElement = formRef.current;
    if (!formElement) {
      alert('Form content is not available for PDF export.');
      setIsPdfGenerating(false);
      return;
    }

    const formClone = formElement.cloneNode(true) as HTMLElement;

    try {
      formClone.style.position = 'absolute';
      formClone.style.left = '-9999px';
      formClone.style.top = '0px';
      formClone.style.width = `${formElement.offsetWidth}px`;
      document.body.appendChild(formClone);

      const inputs = formClone.querySelectorAll('input[type="text"], input[type="number"], textarea');
      inputs.forEach(input => {
        const typedInput = input as HTMLInputElement | HTMLTextAreaElement;
        const value = typedInput.value;
        if (value) {
          const span = document.createElement('span');
          span.textContent = value;
          span.style.borderBottom = '1px solid #333';
          span.style.padding = '2px 5px';
          span.style.margin = '0 5px';
          span.style.fontSize = 'inherit';
          span.style.fontFamily = 'inherit';
          span.style.display = 'inline-block';
          span.style.whiteSpace = 'pre-wrap';
          span.style.wordBreak = 'break-word';
          span.style.color = window.getComputedStyle(typedInput).color;
          typedInput.parentNode?.replaceChild(span, typedInput);
        }
      });

      const checkboxes = formClone.querySelectorAll('input[type="checkbox"], input[type="radio"]');
      checkboxes.forEach(checkbox => {
        const typedCheckbox = checkbox as HTMLInputElement;
        const span = document.createElement('span');
        span.textContent = typedCheckbox.checked ? '☑' : '☐';
        span.style.fontSize = 'inherit';
        span.style.fontFamily = 'inherit';
        span.style.display = 'inline-block';
        span.style.margin = '0 5px';
        typedCheckbox.parentNode?.replaceChild(span, typedCheckbox);
      });

      const buttons = formClone.querySelectorAll('button');
      buttons.forEach(btn => (btn.style.visibility = 'hidden'));

      const canvas = await html2canvas(formClone, {
        scale: 2,
        useCORS: true,
        width: formClone.scrollWidth,
        height: formClone.scrollHeight,
        windowWidth: formClone.scrollWidth,
        windowHeight: formClone.scrollHeight,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.5); // Use aggressive JPEG compression
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'l' : 'p',
        unit: 'px',
        format: [canvas.width, canvas.height],
      });

      pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height);
      
      // Simulate compression process for visual feedback
      for (let i = 0; i <= 100; i += 10) {
        setCompressionProgress(i);
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      pdf.save(`application_form_page6_${applicationId || 'draft'}.pdf`);
    } catch (error) {
      console.error('PDF Generation Error:', error);
      alert(`Error generating PDF for Page 6. Please check the console for details. Error: ${error instanceof Error ? error.message : String(error)}`);
      // Ensure buttons are visible even if an error occurs
      const buttons = formClone.querySelectorAll('button');
      buttons.forEach(btn => btn.style.visibility = 'visible');
    } finally {
      if (formClone.parentNode === document.body) {
        document.body.removeChild(formClone);
      }
      setIsPdfGenerating(false);
      setCompressionProgress(0);
    }
  };

  const saveToDatabaseCallback = useCallback(async (data: typeof formData, isDraft: boolean, anId?: string) => {
    const idToUse = anId || applicationId;
    if (!idToUse) {
      console.error('Application ID is missing, cannot save.');
      setSaveMessage('Error: Application ID missing.');
      setIsSaving(false);
      return;
    }
    if (!isOnline) {
      console.warn('Offline: Save operation skipped for application ID:', idToUse);
      setSaveMessage('Offline: Changes will be saved when back online.');
      return;
    }
    console.log(`[SaveToDB] Attempting to save. ID: ${idToUse}, isDraft: ${isDraft}`);
    setIsSaving(true);
    const currentSaveMessage = isDraft ? 'Saving Draft...' : 'Submitting Application...';
    setSaveMessage(currentSaveMessage);
    setShowSaveIndicator(true);
    try {
      const formDataToSend = new FormData();
      Object.keys(data).forEach(key => {
        const value = data[key as keyof typeof data];
        if (value instanceof File) {
          formDataToSend.append(key, value, value.name);
        } else if (value !== null && value !== undefined && key !== 'errors') {
          formDataToSend.append(key, String(value));
        }
      });
      if (!formDataToSend.has('applicationNoOfficeUse') && idToUse) {
          formDataToSend.append('applicationNoOfficeUse', idToUse);
      }
      const response = await saveFormData(idToUse, formDataToSend, isDraft);
      if (response.success) {
        const successMsg = isDraft ? 'Draft saved successfully' : 'Application submitted successfully';
        console.log(`[SaveToDB] Success for ID: ${idToUse}. Message: ${successMsg}`);
        setSaveMessage(successMsg);
        if (isDraft && isNewlyGeneratedIdRef.current) {
          isNewlyGeneratedIdRef.current = false;
          console.log('[SaveToDB] Reset isNewlyGeneratedIdRef for ID:', idToUse);
        }
        setTimeout(() => setShowSaveIndicator(false), 2000);
      } else {
        setSaveMessage(response.message || 'Error saving draft');
        setTimeout(() => setShowSaveIndicator(false), 3000);
      }
    } catch (error: any) {
      console.error('Error saving form data:', error);
      setSaveMessage(error.message || 'Error saving data');
      setTimeout(() => setShowSaveIndicator(false), 3000);
    } finally {
      setIsSaving(false);
    }
  }, [applicationId, isOnline, saveFormData]);

  // Effect for initial auto-save of a newly generated ID
  useEffect(() => {
    if (applicationId && isNewlyGeneratedIdRef.current && isOnline) {
      const hasActualData = Object.keys(formData).some(key => {
        const formKey = key as keyof FormData;
        if (formKey === 'errors' || formKey === 'applicationNoOfficeUse') return false;
        return formData[formKey] !== '' && formData[formKey] !== null && formData[formKey] !== undefined;
      });
      if (hasActualData) {
        console.log(`[InitialSaveEffect] Newly generated ID ${applicationId} detected with data. Triggering initial draft save.`);
        saveToDatabaseCallback({ ...formData, applicationNoOfficeUse: applicationId }, true, applicationId);
        // isNewlyGeneratedIdRef.current = false; // Moved to saveToDatabaseCallback on successful save
      } else {
        console.log(`[InitialSaveEffect] Newly generated ID ${applicationId} detected, but no actual data to save yet. Skipping initial draft save.`);
      }
    }
  }, [applicationId, formData, isOnline, saveToDatabaseCallback, isNewlyGeneratedIdRef]);

  const handleFillDummyData = () => {
    console.log('[ApplicationFormPage6 FillDummyData] Filling form with dummy data.');
    const dummyFiles = {
      naOrderCopy: 'dummy_na_order.pdf',
      partitionDistributionDeed: 'dummy_partition_deed.pdf',
      firmAllotmentLetter: 'dummy_firm_allotment.pdf',
      poaAffidavit: 'dummy_poa_affidavit.pdf',
      dateOfBirthProof: 'dummy_dob_proof.jpg',
      residenceProof: 'dummy_residence_proof.png',
      educationalQualificationProof: 'dummy_edu_proof.pdf',
      photoIdAddressProof: 'dummy_photo_id_address.jpg',
    };
    const updatedFormData = {
      ...formData,
      ...dummyFiles,
      errors: {},
    };
    setFormData(updatedFormData);
    setSaveMessage('Dummy data filled. Auto-saving...');
    setShowSaveIndicator(true);
    debouncedSave(updatedFormData);
  };

  const debouncedSave = useCallback((newData: typeof formData) => {
    console.log('[DebouncedSave] Invoked. Clearing previous timeout if any.');
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      console.log('[DebouncedSave] Timeout executed. Checking conditions to save.');
      if (applicationId) {
        console.log(`[DebouncedSave] Conditions met. ApplicationId: ${applicationId}. Proceeding to save.`);
        console.log(`[DebouncedSave] Triggering auto-save (as draft) for ID: ${applicationId}`);
        const dataToSave = { ...newData, applicationNoOfficeUse: applicationId };
        saveToDatabaseCallback(dataToSave, true, applicationId);
      } else {
        console.log(`[DebouncedSave] Conditions NOT met. Skipping save. ApplicationId: ${applicationId}`);
      }
    }, 1000);
  }, [saveToDatabaseCallback, applicationId, saveTimeoutRef]);

  // Removed interval-based auto-save - using navigation-based save modal instead

  // Handle file upload
  const handleFileUpload = async (file: File, fieldName: UploadableField) => {
    console.log(`[handleFileUpload] Starting upload for field: ${fieldName}, file:`, file);
    
    if (!applicationId) {
      console.error('Cannot upload file: Missing application ID');
      setUploadStatus(prev => ({ ...prev, [fieldName]: 'error' }));
      setUploadMessage(prev => ({ ...prev, [fieldName]: 'Error: Missing application ID' }));
      return;
    }

    // Global file type validation - block unsupported file types
    const blockedExtensions = ['.doc', '.docx', '.xlsx', '.xls', '.txt', '.bmp'];
    const blockedMimeTypes = [
      'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'image/bmp'
    ];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (blockedExtensions.includes(fileExtension) || blockedMimeTypes.includes(file.type)) {
      setUploadStatus(prev => ({ ...prev, [fieldName]: 'error' }));
      setUploadMessage(prev => ({ ...prev, [fieldName]: `File type "${fileExtension}" is not supported. Please upload only JPG, JPEG, PNG, or PDF files.` }));
      return;
    }

    try {
      setUploadStatus(prev => ({ ...prev, [fieldName]: 'uploading' }));
      setUploadMessage(prev => ({ ...prev, [fieldName]: 'Uploading...' }));
      setUploadProgress(prev => ({ ...prev, [fieldName]: 0 }));

      const uploadResponse = await uploadFormFile(applicationId, file, fieldName, (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded * 100) / event.total);
          setUploadProgress(prev => ({ ...prev, [fieldName]: percentComplete }));
        }
      });

      console.log(`[Upload Success] Field: ${fieldName}, API Response:`, uploadResponse);
      const filePath = uploadResponse.filePath;
      
      const updatedFormData = { ...formData, [fieldName]: filePath };
      setFormData(updatedFormData);
      
      setUploadStatus(prev => ({ ...prev, [fieldName]: 'success' }));
      setUploadMessage(prev => ({ ...prev, [fieldName]: 'File uploaded successfully' }));
      
      // Auto-save after successful upload
      debouncedSave(updatedFormData);
      
      // Clear success message after a short timeout, but keep status as 'success'
      setTimeout(() => {
        setUploadMessage(prev => ({ ...prev, [fieldName]: '' }));
      }, 500);
      
    } catch (error) {
      console.error(`[Upload Error] Field: ${fieldName}, Error:`, error);
      setUploadStatus(prev => ({ ...prev, [fieldName]: 'error' }));
      setUploadMessage(prev => ({ ...prev, [fieldName]: 'Upload failed. Please try again.' }));
      
      setTimeout(() => {
        setUploadMessage(prev => ({ ...prev, [fieldName]: '' }));
      }, 3000);
    }
  };

  // Handle file changes for all input fields
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: keyof FormData) => {
    if (e.target.files && e.target.files[0]) {
      const originalFile = e.target.files[0];
      
      // Get file extension
      const fileExtension = originalFile.name.split('.').pop()?.toLowerCase() || '';
      
      // Create a new filename based on applicationId and field name
      const safeAppId = applicationId ? applicationId.replace(/[^a-zA-Z0-9]/g, '') : 'temp';
      
      // Format the field name to be consistent with what we show in the preview
      // Remove spaces and capitalize first letter of each word
      const formattedFieldName = String(fieldName)
        .replace(/([A-Z])/g, ' $1') // Insert a space before all caps
        .trim() // Remove leading/trailing spaces
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize first letter
        .join(''); // Join without spaces
      
      // Per user request, filename format: fieldname_ApplicationID.fileextension
      const newFileName = `${formattedFieldName}_${safeAppId}.${fileExtension}`;

      const renamedFile = new File([originalFile], newFileName, {
        type: originalFile.type,
        lastModified: originalFile.lastModified,
      });

      console.log(`[handleFileChange] Renamed file: ${originalFile.name} -> ${newFileName}`);
      
      // All file fields on this page should be uploaded with progress.
      handleFileUpload(renamedFile, fieldName as UploadableField);
    }
  };
  

  
  return (
    <>
      {/* Progress bar */}
      <ProgressBarContainer>
        <ProgressBarFill progress={formProgress} />
      </ProgressBarContainer>
      <ProgressLabel>Page 6/14 • {Math.round(formProgress)}% complete</ProgressLabel>
      
      {/* Zoom controls */}
      <ZoomControlsContainer>
        <ZoomButton onClick={handleZoomIn}>+</ZoomButton>
        <ZoomButton onClick={handleZoomOut}>−</ZoomButton>
      </ZoomControlsContainer>
      
      {/* Save indicator */}
      <SaveIndicator visible={showSaveIndicator}>
        {saveMessage}
      </SaveIndicator>

      {!isOnline && (
        <OfflineIndicator>
          You are currently offline. Changes will not be saved until you reconnect.
        </OfflineIndicator>
      )}
      
      <FloatingBackButton onClick={() => navigate('/form/page5')} aria-label="Back to previous page">
        ←
      </FloatingBackButton>
      
      {showDummyDataButton && (
        <div style={{ position: 'fixed', top: '80px', right: '30px', zIndex: 1001, display: 'block' }}>
          <button 
            onClick={handleFillDummyData} 
            style={{ 
              backgroundColor: '#ffc107', 
              color: '#212529', 
              borderRadius: '4px', 
              padding: '8px 15px',
              border: '1px solid #e0a800',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
            }}
          >
            Fill Dummy Data
          </button>
        </div>
      )}
      
      <FormPageNavigator currentPage={6} totalPages={14} targetFormRef={formRef} zoomScale={zoomScale} />
      
      <div style={{ position: 'fixed', right: '-25px', top: '605px', zIndex: 999 }}>
        <NextPageButton 
          onClick={handleNextButtonClick}
          style={{ 
            height: '35px', 
            width: '140px', 
            borderRadius: '4px', 
            whiteSpace: 'nowrap',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '0 10px'
          }}
        >
          Next Page 7/14 →
        </NextPageButton>
      </div>
      
      <PageWrapper scale={zoomScale}>
        <A4PageContainer ref={formRef}>
          <FormContentContainer>
            <FormTitle>Supporting Documents Upload</FormTitle>
            
            {/* File Upload Fields */}
            <FileUploadContainer>
  <FileLabel>22. NA order as applicable (Copy)</FileLabel>
  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <FileUploadButton htmlFor="naOrderCopy">
        Choose File
        <FileInput
          type="file"
          id="naOrderCopy"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={(e) => handleFileChange(e, 'naOrderCopy')}
          disabled={isViewMode || uploadStatus.naOrderCopy === 'uploading'}
        />
      </FileUploadButton>
      <FileName>{getFileName(formData.naOrderCopy)}</FileName>
      {formData.naOrderCopy && (
        <button onClick={() => handleOpenFilePreview(formData.naOrderCopy, 'NA Order')} style={{ marginLeft: '10px', fontSize: '0.8em' }}>
          Preview
        </button>
      )}
    </div>
    
    {/* Upload status and progress bar */}
    {uploadStatus.naOrderCopy === 'uploading' && (
      <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
        <div 
          style={{ 
            position: 'absolute', 
            height: '100%', 
            width: `${uploadProgress.naOrderCopy || 0}%`, 
            backgroundColor: '#6cb288',
            borderRadius: '4px',
            transition: 'width 0.3s ease-in-out'
          }}
        />
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            color: uploadProgress.naOrderCopy && uploadProgress.naOrderCopy > 50 ? '#fff' : '#000'
          }}
        >
          {uploadProgress.naOrderCopy || 0}%
        </div>
      </div>
    )}
    
    {/* Success or error message */}
    {uploadMessage.naOrderCopy && (
      <div 
        style={{ 
          padding: '4px', 
          backgroundColor: uploadStatus.naOrderCopy === 'error' ? '#f8d7da' : '#d4edda',
          color: uploadStatus.naOrderCopy === 'error' ? '#721c24' : '#155724',
          borderRadius: '4px',
          fontSize: '12px',
          marginTop: '4px'
        }}
      >
        {uploadMessage.naOrderCopy}
      </div>
    )}
  </div>
</FileUploadContainer>

            <FileUploadContainer>
              <FileLabel>23. Partition/Distribution Deed (In case of Family Partition / Distribution, Certified Copy)</FileLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FileUploadButton htmlFor="partitionDeed">
                    Choose File
                    <FileInput
                      type="file"
                      id="partitionDeed"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, 'partitionDistributionDeed')}
                      disabled={isViewMode || uploadStatus.partitionDistributionDeed === 'uploading'}
                    />
                  </FileUploadButton>
                  <FileName>{getFileName(formData.partitionDistributionDeed)}</FileName>
                  {formData.partitionDistributionDeed && (
                    <button onClick={() => handleOpenFilePreview(formData.partitionDistributionDeed, 'Partition Deed')} style={{ marginLeft: '10px', fontSize: '0.8em' }}>
                      Preview
                    </button>
                  )}
                </div>
                {uploadStatus.partitionDistributionDeed === 'uploading' && (
                  <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                    <div style={{ position: 'absolute', height: '100%', width: `${uploadProgress.partitionDistributionDeed || 0}%`, backgroundColor: '#6cb288', borderRadius: '4px', transition: 'width 0.3s ease-in-out' }} />
                    <div style={{ position: 'absolute', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: uploadProgress.partitionDistributionDeed && uploadProgress.partitionDistributionDeed > 50 ? '#fff' : '#000' }}>
                      {uploadProgress.partitionDistributionDeed || 0}%
                    </div>
                  </div>
                )}
                {uploadMessage.partitionDistributionDeed && (
                  <div style={{ padding: '4px', backgroundColor: uploadStatus.partitionDistributionDeed === 'error' ? '#f8d7da' : '#d4edda', color: uploadStatus.partitionDistributionDeed === 'error' ? '#721c24' : '#155724', borderRadius: '4px', fontSize: '12px', marginTop: '4px' }}>
                    {uploadMessage.partitionDistributionDeed}
                  </div>
                )}
              </div>
            </FileUploadContainer>

            <FileUploadContainer>
              <FileLabel>24. Firm "allotment letter" issued by Govt/Semi-Govt bodies (if applicable, Certified Copy)</FileLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FileUploadButton htmlFor="firmAllotmentLetter">
                    Choose File
                    <FileInput
                      type="file"
                      id="firmAllotmentLetter"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, 'firmAllotmentLetter')}
                      disabled={isViewMode || uploadStatus.firmAllotmentLetter === 'uploading'}
                    />
                  </FileUploadButton>
                  <FileName>{getFileName(formData.firmAllotmentLetter)}</FileName>
                  {formData.firmAllotmentLetter && (
                    <button onClick={() => handleOpenFilePreview(formData.firmAllotmentLetter, 'Allotment Letter')} style={{ marginLeft: '10px', fontSize: '0.8em' }}>
                      Preview
                    </button>
                  )}
                </div>
                {uploadStatus.firmAllotmentLetter === 'uploading' && (
                  <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                    <div style={{ position: 'absolute', height: '100%', width: `${uploadProgress.firmAllotmentLetter || 0}%`, backgroundColor: '#6cb288', borderRadius: '4px', transition: 'width 0.3s ease-in-out' }} />
                    <div style={{ position: 'absolute', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: uploadProgress.firmAllotmentLetter && uploadProgress.firmAllotmentLetter > 50 ? '#fff' : '#000' }}>
                      {uploadProgress.firmAllotmentLetter || 0}%
                    </div>
                  </div>
                )}
                {uploadMessage.firmAllotmentLetter && (
                  <div style={{ padding: '4px', backgroundColor: uploadStatus.firmAllotmentLetter === 'error' ? '#f8d7da' : '#d4edda', color: uploadStatus.firmAllotmentLetter === 'error' ? '#721c24' : '#155724', borderRadius: '4px', fontSize: '12px', marginTop: '4px' }}>
                    {uploadMessage.firmAllotmentLetter}
                  </div>
                )}
              </div>
            </FileUploadContainer>

            <FileUploadContainer>
              <FileLabel>25. In case Applicant is registered power of Attorney Holder then Notarized Affidavit from all owners stating that PoA is not revoked / cancelled / terminated and is still in existence - (Appendix E) on Rs. 100 stamp paper (Certified Copy of Registered PoA)</FileLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FileUploadButton htmlFor="poaAffidavit">
                    Choose File
                    <FileInput
                      type="file"
                      id="poaAffidavit"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, 'poaAffidavit')}
                      disabled={isViewMode || uploadStatus.poaAffidavit === 'uploading'}
                    />
                  </FileUploadButton>
                  <FileName>{getFileName(formData.poaAffidavit)}</FileName>
                  {formData.poaAffidavit && (
                    <button onClick={() => handleOpenFilePreview(formData.poaAffidavit, 'PoA Affidavit')} style={{ marginLeft: '10px', fontSize: '0.8em' }}>
                      Preview
                    </button>
                  )}
                </div>
                {uploadStatus.poaAffidavit === 'uploading' && (
                  <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                    <div style={{ position: 'absolute', height: '100%', width: `${uploadProgress.poaAffidavit || 0}%`, backgroundColor: '#6cb288', borderRadius: '4px', transition: 'width 0.3s ease-in-out' }} />
                    <div style={{ position: 'absolute', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: uploadProgress.poaAffidavit && uploadProgress.poaAffidavit > 50 ? '#fff' : '#000' }}>
                      {uploadProgress.poaAffidavit || 0}%
                    </div>
                  </div>
                )}
                {uploadMessage.poaAffidavit && (
                  <div style={{ padding: '4px', backgroundColor: uploadStatus.poaAffidavit === 'error' ? '#f8d7da' : '#d4edda', color: uploadStatus.poaAffidavit === 'error' ? '#721c24' : '#155724', borderRadius: '4px', fontSize: '12px', marginTop: '4px' }}>
                    {uploadMessage.poaAffidavit}
                  </div>
                )}
              </div>
            </FileUploadContainer>

            <FileUploadContainer>
              <FileLabel>26. Proof of Date of Birth (Copy) *</FileLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FileUploadButton htmlFor="dateOfBirthProof">
                    Choose File
                    <FileInput
                      type="file"
                      id="dateOfBirthProof"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, 'dateOfBirthProof')}
                      disabled={isViewMode || uploadStatus.dateOfBirthProof === 'uploading'}
                    />
                  </FileUploadButton>
                  <FileName>{getFileName(formData.dateOfBirthProof)}</FileName>
                  {formData.dateOfBirthProof && (
                    <button onClick={() => handleOpenFilePreview(formData.dateOfBirthProof, 'Date of Birth Proof')} style={{ marginLeft: '10px', fontSize: '0.8em' }}>
                      Preview
                    </button>
                  )}
                </div>
                {uploadStatus.dateOfBirthProof === 'uploading' && (
                  <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                    <div style={{ position: 'absolute', height: '100%', width: `${uploadProgress.dateOfBirthProof || 0}%`, backgroundColor: '#6cb288', borderRadius: '4px', transition: 'width 0.3s ease-in-out' }} />
                    <div style={{ position: 'absolute', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: uploadProgress.dateOfBirthProof && uploadProgress.dateOfBirthProof > 50 ? '#fff' : '#000' }}>
                      {uploadProgress.dateOfBirthProof || 0}%
                    </div>
                  </div>
                )}
                {uploadMessage.dateOfBirthProof && (
                  <div style={{ padding: '4px', backgroundColor: uploadStatus.dateOfBirthProof === 'error' ? '#f8d7da' : '#d4edda', color: uploadStatus.dateOfBirthProof === 'error' ? '#721c24' : '#155724', borderRadius: '4px', fontSize: '12px', marginTop: '4px' }}>
                    {uploadMessage.dateOfBirthProof}
                  </div>
                )}
              </div>
            </FileUploadContainer>

            <FileUploadContainer>
              <FileLabel>27. Proof of Residence (Copy) *</FileLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FileUploadButton htmlFor="residenceProof">
                    Choose File
                    <FileInput
                      type="file"
                      id="residenceProof"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, 'residenceProof')}
                      disabled={isViewMode || uploadStatus.residenceProof === 'uploading'}
                    />
                  </FileUploadButton>
                  <FileName>{getFileName(formData.residenceProof)}</FileName>
                  {formData.residenceProof && (
                    <button onClick={() => handleOpenFilePreview(formData.residenceProof, 'Residence Proof')} style={{ marginLeft: '10px', fontSize: '0.8em' }}>
                      Preview
                    </button>
                  )}
                </div>
                {uploadStatus.residenceProof === 'uploading' && (
                  <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                    <div style={{ position: 'absolute', height: '100%', width: `${uploadProgress.residenceProof || 0}%`, backgroundColor: '#6cb288', borderRadius: '4px', transition: 'width 0.3s ease-in-out' }} />
                    <div style={{ position: 'absolute', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: uploadProgress.residenceProof && uploadProgress.residenceProof > 50 ? '#fff' : '#000' }}>
                      {uploadProgress.residenceProof || 0}%
                    </div>
                  </div>
                )}
                {uploadMessage.residenceProof && (
                  <div style={{ padding: '4px', backgroundColor: uploadStatus.residenceProof === 'error' ? '#f8d7da' : '#d4edda', color: uploadStatus.residenceProof === 'error' ? '#721c24' : '#155724', borderRadius: '4px', fontSize: '12px', marginTop: '4px' }}>
                    {uploadMessage.residenceProof}
                  </div>
                )}
              </div>
            </FileUploadContainer>

            <FileUploadContainer>
              <FileLabel>28. Proof of Educational Qualification (Copy) *</FileLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FileUploadButton htmlFor="educationalQualificationProof">
                    Choose File
                    <FileInput
                      type="file"
                      id="educationalQualificationProof"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, 'educationalQualificationProof')}
                      disabled={isViewMode || uploadStatus.educationalQualificationProof === 'uploading'}
                    />
                  </FileUploadButton>
                  <FileName>{getFileName(formData.educationalQualificationProof)}</FileName>
                  {formData.educationalQualificationProof && (
                    <button onClick={() => handleOpenFilePreview(formData.educationalQualificationProof, 'Educational Qualification')} style={{ marginLeft: '10px', fontSize: '0.8em' }}>
                      Preview
                    </button>
                  )}
                </div>
                {uploadStatus.educationalQualificationProof === 'uploading' && (
                  <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                    <div style={{ position: 'absolute', height: '100%', width: `${uploadProgress.educationalQualificationProof || 0}%`, backgroundColor: '#6cb288', borderRadius: '4px', transition: 'width 0.3s ease-in-out' }} />
                    <div style={{ position: 'absolute', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: uploadProgress.educationalQualificationProof && uploadProgress.educationalQualificationProof > 50 ? '#fff' : '#000' }}>
                      {uploadProgress.educationalQualificationProof || 0}%
                    </div>
                  </div>
                )}
                {uploadMessage.educationalQualificationProof && (
                  <div style={{ padding: '4px', backgroundColor: uploadStatus.educationalQualificationProof === 'error' ? '#f8d7da' : '#d4edda', color: uploadStatus.educationalQualificationProof === 'error' ? '#721c24' : '#155724', borderRadius: '4px', fontSize: '12px', marginTop: '4px' }}>
                    {uploadMessage.educationalQualificationProof}
                  </div>
                )}
              </div>
            </FileUploadContainer>

            <FileUploadContainer>
              <FileLabel>29. Proof of Photo Id & address (copy - Aadhar card) *</FileLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FileUploadButton htmlFor="photoIdAddressProof">
                    Choose File
                    <FileInput
                      type="file"
                      id="photoIdAddressProof"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, 'photoIdAddressProof')}
                      disabled={isViewMode || uploadStatus.photoIdAddressProof === 'uploading'}
                    />
                  </FileUploadButton>
                  <FileName>{getFileName(formData.photoIdAddressProof)}</FileName>
                  {formData.photoIdAddressProof && (
                    <button onClick={() => handleOpenFilePreview(formData.photoIdAddressProof, 'Photo ID & Address Proof')} style={{ marginLeft: '10px', fontSize: '0.8em' }}>
                      Preview
                    </button>
                  )}
                </div>
                {uploadStatus.photoIdAddressProof === 'uploading' && (
                  <div style={{ position: 'relative', height: '20px', width: '100%', backgroundColor: '#e9ecef', borderRadius: '4px', marginTop: '4px' }}>
                    <div style={{ position: 'absolute', height: '100%', width: `${uploadProgress.photoIdAddressProof || 0}%`, backgroundColor: '#6cb288', borderRadius: '4px', transition: 'width 0.3s ease-in-out' }} />
                    <div style={{ position: 'absolute', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: uploadProgress.photoIdAddressProof && uploadProgress.photoIdAddressProof > 50 ? '#fff' : '#000' }}>
                      {uploadProgress.photoIdAddressProof || 0}%
                    </div>
                  </div>
                )}
                {uploadMessage.photoIdAddressProof && (
                  <div style={{ padding: '4px', backgroundColor: uploadStatus.photoIdAddressProof === 'error' ? '#f8d7da' : '#d4edda', color: uploadStatus.photoIdAddressProof === 'error' ? '#721c24' : '#155724', borderRadius: '4px', fontSize: '12px', marginTop: '4px' }}>
                    {uploadMessage.photoIdAddressProof}
                  </div>
                )}
              </div>
            </FileUploadContainer>

            {/* Notes Section */}
            <NotesContainer>
              <NotesTitle>Important Notes</NotesTitle>
              <NotesList>
                <NoteItem>
                  The Application Form is to be submitted along with a non-refundable processing fee of Rs. 5000.00 by way of Demand Draft/Cheque (in favour of 'Maharashtra Natural Gas Limited' payable at Pune).
                </NoteItem>
                <NoteItem>
                  Preference will be given to land situated on/nearby MNGL existing and proposed pipeline.
                </NoteItem>
                <NoteItem>
                  Property tax and other statutory charges on the property and on lease rental will be borne by the applicant/landowner.
                </NoteItem>
                <NoteItem>
                  No addition/deletion/alteration will be permitted in the application once it is submitted. Also, no strike-outs, cancellations, overwritings are permitted in the application form.
                </NoteItem>
              </NotesList>
            </NotesContainer>
          </FormContentContainer>
          <PageNumber>6</PageNumber>
        </A4PageContainer>
        
        <FilePreviewModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          fileUrl={modalFileUrl}
          fileName={modalFileName}
          fileType={modalFileType}
        />
      </PageWrapper>
      
      {/* Empty ActionButtonsContainer to maintain structure */}
      <ActionButtonsContainer style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '800px', zIndex: 1000 }}>
        {/* Next Page button moved near FormPageNavigator */}
      </ActionButtonsContainer>
      
      {/* Session Timer */}
      {!isViewMode && (
        <SessionTimer 
          sessionDuration={30}
          warningTime={5}
          onSessionExpired={() => navigate('/')}
          onSaveState={async () => {
            if (applicationId) {
              await saveToDatabaseCallback(formData, true, applicationId);
            }
          }}
        />
      )}

      {/* Home Button - Above Download PDF */}
      <button
        onClick={() => navigate('/user-dashboard')}
        title="Go to Dashboard"
        style={{
          position: 'fixed',
          left: '95px',
          bottom: isViewMode ? '100px' : '175px',
          zIndex: 1000,
          width: '145px',
          height: '45px',
          borderRadius: '50px',
          border: 'none',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '13px',
          fontWeight: '600',
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
          transition: 'all 0.2s ease'
        }}
      >
        🏠 Dashboard
      </button>

      {!isViewMode && (
        <>
          <PayNowButton 
            onClick={handleDownloadPdf} 
            disabled={!isOnline || isViewMode}
            style={{ position: 'fixed', left: '95px', bottom: '105px', zIndex: 1000, margin: '0', height: '57px', borderRadius: '4px', width: '145px', fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center', fontWeight: 'bold', letterSpacing: '0.2px' }}
          >
            DOWNLOAD PDF
          </PayNowButton>
          
          <SaveDraftButton 
            onClick={() => saveToDatabaseCallback(formData, true, applicationId)} 
            disabled={isSaving || !applicationId || !isOnline}
            style={{ position: 'fixed', left: '95px', bottom: '31.5px', zIndex: 1000, margin: '0', height: '57px', borderRadius: '4px', width: '145px', textAlign: 'center', fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.2px' }}
          >
            {isSaving && saveMessage.startsWith('Saving Draft') ? 'Saving...' : 'SAVE DRAFT'}
          </SaveDraftButton>
        </>
      )}
      
      {isViewMode && (
        <PayNowButton 
          onClick={handleDownloadPdf} 
          disabled={!isOnline}
          style={{ position: 'fixed', left: '95px', bottom: '31.5px', zIndex: 1000, margin: '0', height: '57px', borderRadius: '4px', width: '145px', fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center', fontWeight: 'bold', letterSpacing: '0.2px' }}
        > 
          DOWNLOAD PDF
        </PayNowButton>
      )}
      
      {isPdfGenerating && (
        <ModalOverlay>
          <ModalContent>
            <h3>Generating PDF</h3>
            <p>Please wait... Generating PDF</p>
            <ProgressBarContainerModal>
              <ProgressBarFillModal progress={compressionProgress} />
            </ProgressBarContainerModal>
            <ProgressText>Compression: {compressionProgress}%</ProgressText>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
`;

const ProgressBarContainerModal = styled.div`
  width: 100%;
  height: 20px;
  background-color: #e9ecef;
  border-radius: 10px;
  margin-top: 20px;
  overflow: hidden;
`;

const ProgressBarFillModal = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => props.progress}%; // Dynamic width based on progress
  background-color: #6cb288;
  transition: width 0.3s ease-in-out;
`;

const ProgressText = styled.p`
  margin-top: 10px;
  font-size: 0.9em;
  color: #555;
`;

export default ApplicationFormPage6;